<template>
  <v-row dense>
    <v-col :md="modelValue.length ? 8 : 12" cols="12">
      <v-autocomplete
        :model-value="modelValue"
        @update:model-value="emit('update:modelValue', $event)"
        :items="items"
        @update:search="emit('update:search', $event)"
        :custom-filter="() => true"
        label="Adicionar Participantes ou Grupos"
        color="primary20"
        variant="outlined"
        density="compact"
        clear-on-select
        :menu-props="{ closeOnContentClick: true, maxHeight: 300 }"
        chips
        closable-chips
        multiple
        hide-details
        clearable
        :disabled="disabled"
        :loading="searching"
        no-data-text="Nenhum usuário encontrado"
      >
        <template v-slot:chip="{ props, item }">
          <v-chip
            color="primary30"
            v-bind="props"
            :prepend-icon="item.raw.icon"
            :text="item.raw.title"
          />
        </template>

        <template v-slot:item="{ props, item }">
          <v-list-item
            v-bind="props"
            :prepend-icon="item.raw.icon"
            :subtitle="item.raw.subtitle"
            :title="item.raw.title"
          />
        </template>

        <template v-slot:append-item>
          <div
            v-if="hasMore"
            v-intersect="onEndReached"
            class="pa-4 text-center flex justify-center w-full"
          >
            <v-progress-circular
              v-if="loadingMore"
              indeterminate
              size="24"
              width="2"
              color="primary"
            />
          </div>
        </template>
      </v-autocomplete>
    </v-col>

    <v-col v-if="modelValue.length" cols="12" md="4">
      <v-autocomplete
        :model-value="permission"
        @update:model-value="emit('update:permission', $event)"
        auto-select-first
        :items="permissionOptions"
        item-title="label"
        item-value="value"
        color="primary20"
        density="compact"
        hide-details
        chips
        variant="outlined"
        required
      >
        <template v-slot:chip="{ props, item }">
          <span class="flex items-center text-sm" v-bind="props">
            {{ item.raw.label }}
          </span>
        </template>
      </v-autocomplete>
    </v-col>

    <v-col cols="12" v-if="impactWarnings.length > 0">
      <v-alert
        type="warning"
        variant="tonal"
        color="alert"
        class="mt-2 text-left"
        density="compact"
        border="start"
      >
        <template #title>
          <span class="text-sm font-bold">Atenção: Impacto em permissões existentes</span>
        </template>
        <div class="text-xs mt-1 max-h-[100px] overflow-y-auto">
          <ul class="list-disc ml-4">
            <li v-for="(warn, index) in impactWarnings" :key="index">
              O usuário/grupo
              <strong>{{ getSubjectName(warn.subject_id, warn.subject_type) }}</strong>
              já possui permissão de
              <strong>{{ getRelationLabel(warn.current_relation) }}</strong> em
              <strong>{{ warn.resource_name || 'Recurso relacionado' }}</strong
              >.
            </li>
          </ul>
        </div>
      </v-alert>
    </v-col>
  </v-row>
</template>

<script setup lang="ts">
import {
  AuthorizationRelations,
  AuthorizationResourcesAndSubjects,
  PermissionImpactResponse
} from '@/types/authorization'

export interface SubjectItem {
  title: string
  subtitle: string
  value: { id: string; type: AuthorizationResourcesAndSubjects }
  icon: string
  raw: Record<string, any>
}

export interface PermissionOption {
  label: string
  value: AuthorizationRelations
}

export type SelectedSubject = { id: string; type: AuthorizationResourcesAndSubjects }

const props = withDefaults(
  defineProps<{
    modelValue: SelectedSubject[]
    permission: AuthorizationRelations
    items: SubjectItem[]
    permissionOptions: PermissionOption[]
    search?: string
    disabled?: boolean
    searching?: boolean
    hasMore?: boolean
    loadingMore?: boolean
    impactWarnings?: PermissionImpactResponse[]
    getSubjectName: (id: number, type: string) => string
    getRelationLabel: (relation: string) => string
  }>(),
  { impactWarnings: () => [] }
)

const emit = defineEmits<{
  'update:modelValue': [value: SelectedSubject[]]
  'update:permission': [value: AuthorizationRelations]
  'update:search': [value: string]
  'end-reached': []
}>()

const onEndReached = (isIntersecting: boolean) => {
  if (isIntersecting && props.hasMore && !props.loadingMore) {
    emit('end-reached')
  }
}
</script>
